import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Rewind, FastForward, Gauge, Volume2 } from "lucide-react";
import { resolveFileUrl } from "../lib/api";

const SPEEDS = [0.8, 1.0, 1.25, 1.5, 2.0];

export default function AudioPlayer({ src, compact = false, onPlayState, apiRef }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    if (apiRef) {
      apiRef.current = {
        seek: (t) => { if (audioRef.current) { audioRef.current.currentTime = t; audioRef.current.play(); } },
        toggle: () => { if (audioRef.current) { playing ? audioRef.current.pause() : audioRef.current.play(); } },
        isPlaying: () => playing,
      };
    }
  }, [apiRef, playing]);

  const fullUrl = resolveFileUrl(src);

  const toggle = () => { if (!audioRef.current) return; if (playing) audioRef.current.pause(); else audioRef.current.play(); };
  const skip = (sec) => { if (!audioRef.current) return; audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + sec)); };
  const setSp = () => {
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (!src) {
    return (
      <div className={`audio-player ${compact ? "compact" : ""}`} data-testid="audio-player-empty">
        <Volume2 size={20} className="text-[#55594F]" />
        <div className="text-base text-[#55594F]">Audio aún no disponible.</div>
      </div>
    );
  }

  return (
    <div className={`audio-player ${compact ? "compact" : ""}`} data-testid="audio-player">
      <audio
        ref={audioRef}
        src={fullUrl}
        onPlay={() => { setPlaying(true); onPlayState?.(true); }}
        onPause={() => { setPlaying(false); onPlayState?.(false); }}
        onEnded={() => { setPlaying(false); onPlayState?.(false); }}
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        preload="metadata"
      />
      <button onClick={() => skip(-15)} className="pill pill-xs pill-ghost" aria-label="Retroceder 15 s" data-testid="btn-rew-15">
        <Rewind size={18} /> <span className="text-xs font-mono">15</span>
      </button>
      <button onClick={toggle} className="pill pill-gold !h-14 !px-6" aria-label={playing ? "Pausar" : "Reproducir"} data-testid="btn-play-pause">
        {playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
      </button>
      <button onClick={() => skip(15)} className="pill pill-xs pill-ghost" aria-label="Avanzar 15 s" data-testid="btn-fwd-15">
        <span className="text-xs font-mono">15</span> <FastForward size={18} />
      </button>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <span className="text-xs font-mono text-[#55594F] w-10 text-right" data-testid="audio-current">{fmt(progress)}</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          value={progress}
          onChange={(e) => { if (audioRef.current) audioRef.current.currentTime = parseFloat(e.target.value); }}
          className="w-full accent-[#1B1C1A]"
          data-testid="audio-seek"
        />
        <span className="text-xs font-mono text-[#55594F] w-10" data-testid="audio-duration">{fmt(duration)}</span>
      </div>
      <button onClick={setSp} className="pill pill-xs pill-ghost" aria-label="Velocidad" data-testid="btn-speed">
        <Gauge size={16} /> <span className="font-mono text-xs">{speed}×</span>
      </button>
    </div>
  );
}
